import React from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import { ReactComponent as MiniLogo } from "@app/assets/logo-mini.svg";
import Chip from "@app/component/Chip";
import UserPreviewCard from "@app/component/UserPreviewCard";
import Path from "@app/enum/Path";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { getOrganizationName, isCurrentUserOwner } from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";
import { userDisplayName } from "@app/util/formatters";

import styles from "./Title.module.scss";

const messages = defineMessages({
  home: {
    id: "dashboard.sidebar.title",
    defaultMessage: "Home",
    description: "Title of the Dashboard page",
  },
});

interface Props {
  isOwner: boolean;
  organizationName: string;
  scope: Scope;
  teams: Team[];
  users: User[];
  view: ViewType;
}

const Title: React.FC<Props> = ({ isOwner, organizationName, scope, teams, users, view }) => {
  const intl = useIntl();

  const team = scope.teamId ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const user = scope.userId ? users.find(({ id }) => id === scope.userId) : undefined;

  return (
    <div className={styles.container} data-item-id="stack-rank-title">
      <span className={styles.title}>{intl.formatMessage(messages.home)}</span>
      <Chip className={styles.breadcrumbsContainer}>
        <div className={styles.breadcrumbs}>
          <MiniLogo className={styles.noShrink} />/
          <Tooltip title={organizationName}>
            {isOwner ? (
              <Link
                className={cn(styles.shrink, styles.team)}
                to={`${Path.DASHBOARD}/${view !== ViewType.DASHBOARD ? `/${view}` : ""}`}
              >
                {organizationName}
              </Link>
            ) : (
              <span>{organizationName}</span>
            )}
          </Tooltip>
          {team && (
            <>
              /
              <Tooltip title={team.name}>
                <Link
                  className={cn(styles.shrink, styles.team)}
                  to={`${Path.DASHBOARD}/teams/${team.id}${
                    view !== ViewType.DASHBOARD ? `/${view}` : ""
                  }`}
                >
                  {team.name}
                </Link>
              </Tooltip>
            </>
          )}
          {user && (
            <>
              /
              <UserPreviewCard userId={user.id}>
                <Tooltip title={userDisplayName(user)}>
                  <span className={styles.shrink}>{userDisplayName(user)}</span>
                </Tooltip>
              </UserPreviewCard>
            </>
          )}
        </div>
      </Chip>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  organizationName: getOrganizationName(state)!,
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(Title);
